import { useState } from 'react';
import { useI18n } from '../../../../i18n';
import { adminRequest } from '../../../shared/services/api';

// ═══════════════════════════════════════════════════════════════
// CrearAdminTenantModal — prompt 0523
// Modal para crear un usuario admin (rol=CEO) en un tenant ajeno.
// SUPERADMIN_PLATFORM only (filtro en BackofficePage).
// Tras crear muestra password temporal con botón copiar.
// ═══════════════════════════════════════════════════════════════

interface CreatedUser {
  usuario: {
    id: string;
    email: string;
    nombreCompleto: string;
    rol: string;
    tenantId: string;
  };
  passwordTemporal: string;
  tenantNombre: string;
  tenantSlug: string;
}

interface Props {
  tenantId: string;
  tenantNombre: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const OVERLAY: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  fontFamily: 'DM Sans, sans-serif',
};

const CARD: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  padding: '28px 32px',
  width: '100%',
  maxWidth: 480,
};

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid #D1D5DB',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  color: '#111827',
  outline: 'none',
};

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const FIELD: React.CSSProperties = { marginBottom: 16 };

const BTN_PRIMARY: React.CSSProperties = {
  padding: '10px 20px',
  background: '#7C3AED',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const BTN_SECONDARY: React.CSSProperties = {
  padding: '10px 20px',
  background: 'transparent',
  color: '#6B7280',
  border: '1px solid #D1D5DB',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export function CrearAdminTenantModal({ tenantId, tenantNombre, onClose, onSuccess }: Props) {
  const { t } = useI18n();
  const a = t.backoffice.addAdmin;

  const [email, setEmail] = useState('');
  const [nombreCompleto, setNombreCompleto] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedUser | null>(null);
  const [copiado, setCopiado] = useState(false);

  const canSubmit = email.includes('@') && nombreCompleto.trim().length >= 2 && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminRequest<{ data: CreatedUser }>(`/tenants/${tenantId}/admin-users`, {
        method: 'POST',
        body: JSON.stringify({ email, nombreCompleto }),
      });
      setCreated(res.data);
      onSuccess?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.passwordTemporal);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // fallback: seleccionar el texto en un input para copia manual
      const el = document.getElementById('admin-pass-display') as HTMLInputElement | null;
      el?.select();
    }
  };

  // Pantalla éxito con password
  if (created) {
    return (
      <div style={OVERLAY} onClick={onClose}>
        <div style={CARD} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1F3864', margin: 0 }}>
            {a.exitoTitulo}
          </h3>
          <p style={{ fontSize: 13, color: '#6B7280', marginTop: 8, marginBottom: 20 }}>
            {a.exitoDescripcion}
          </p>

          <div
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              border: '1px dashed #7C3AED',
              borderRadius: 8,
              background: '#F5F3FF',
            }}
          >
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>{a.passwordLabel}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                id="admin-pass-display"
                data-testid="addadmin-password"
                readOnly
                value={created.passwordTemporal}
                style={{
                  ...INPUT,
                  fontFamily: 'monospace',
                  fontSize: 13,
                  background: '#fff',
                  flex: 1,
                }}
              />
              <button
                data-testid="addadmin-btn-copy"
                onClick={handleCopy}
                style={{
                  ...BTN_SECONDARY,
                  padding: '8px 14px',
                  color: copiado ? '#15803D' : '#7C3AED',
                  borderColor: copiado ? '#15803D' : '#7C3AED',
                  whiteSpace: 'nowrap',
                }}
              >
                {copiado ? a.copiado : a.copiarPassword}
              </button>
            </div>
          </div>

          <div style={{ fontSize: 13, color: '#374151', marginBottom: 20 }}>
            <strong>{t.admin.usuarios.email}:</strong> {created.usuario.email}
            <br />
            <strong>{t.admin.usuarios.nombre}:</strong> {created.usuario.nombreCompleto}
            <br />
            <strong>{t.admin.usuarios.rol}:</strong> {created.usuario.rol}
            <br />
            <strong>{t.backoffice.gestionTenants}:</strong> {created.tenantNombre}
          </div>

          <button
            data-testid="addadmin-btn-cerrar"
            style={{ ...BTN_PRIMARY, width: '100%' }}
            onClick={onClose}
          >
            {a.cerrar}
          </button>
        </div>
      </div>
    );
  }

  // Form
  return (
    <div style={OVERLAY} onClick={onClose}>
      <div style={CARD} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1F3864', margin: 0 }}>
          {a.titulo} <span style={{ color: '#7C3AED' }}>{tenantNombre}</span>
        </h3>
        <p style={{ fontSize: 13, color: '#6B7280', marginTop: 8, marginBottom: 20 }}>
          {a.subtitulo}
        </p>

        <div style={FIELD}>
          <label style={LABEL}>{a.email} *</label>
          <input
            data-testid="addadmin-input-email"
            type="email"
            style={INPUT}
            value={email}
            placeholder={a.emailPlaceholder}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div style={FIELD}>
          <label style={LABEL}>{a.nombre} *</label>
          <input
            data-testid="addadmin-input-nombre"
            type="text"
            style={INPUT}
            value={nombreCompleto}
            placeholder={a.nombrePlaceholder}
            onChange={(e) => setNombreCompleto(e.target.value)}
          />
        </div>

        {error && (
          <div
            data-testid="addadmin-error"
            style={{
              marginBottom: 16,
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
              background: '#FEF2F2',
              color: '#DC2626',
              border: '1px solid #FECACA',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            data-testid="addadmin-btn-cancelar"
            style={BTN_SECONDARY}
            onClick={onClose}
            disabled={submitting}
          >
            {a.cancelar}
          </button>
          <button
            data-testid="addadmin-btn-crear"
            style={{ ...BTN_PRIMARY, opacity: canSubmit ? 1 : 0.5 }}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? a.creando : a.crear}
          </button>
        </div>
      </div>
    </div>
  );
}
